import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import Swal from "sweetalert2";
import "./CourseStyle.css";
import "react-quill/dist/quill.snow.css";
import Loading from "../../utils/Loading";

import { useAuthContext } from "../../hooks/useAuthContext";

import { useNavigate } from "react-router-dom";
import { COURSE_BASE_URL } from "../../services/courseService";

const AddCourse = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { user } = useAuthContext();

  const [formData, setFormData] = useState({
    title: "",
    instructor: "",
    description: "",
    credit: "",
    price: "",
    category: "",
    numberOfCourses: 1,
    courseRating: 0,
    hasPaid: false,

    lessons: [
      {
        lessonTitle: "",
        topics: [
          {
            title: "",
            content: "",
          },
        ],
      },
    ],
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleLessonChange = (index, e) => {
    const { name, value } = e.target;
    const newLessons = [...formData.lessons];
    newLessons[index][name] = value;
    setFormData({ ...formData, lessons: newLessons });
  };

  const handleTopicChange = (lessonIndex, topicIndex, e) => {
    const { name, value } = e.target;
    const newLessons = [...formData.lessons];
    newLessons[lessonIndex].topics[topicIndex][name] = value;
    setFormData({ ...formData, lessons: newLessons });
  };

  const addLesson = () => {
    setFormData({
      ...formData,
      lessons: [
        ...formData.lessons,
        { lessonTitle: "", topics: [{ title: "", content: "" }] },
      ],
    });
  };

  const addTopic = (lessonIndex) => {
    const newLessons = [...formData.lessons];
    newLessons[lessonIndex].topics.push({ title: "", content: "" });
    setFormData({ ...formData, lessons: newLessons });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setError(null);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${COURSE_BASE_URL}/api/courses/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(formData), // Include form data in the request body
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Course created successfully:", result);
      setError(null);
      setLoading(false);
      Swal.fire({
        position: "top-center",
        icon: "success",
        title: "Success",
        text: "New course added successfully!",
        showConfirmButton: false,
        timer: 2500,
      });
      setTimeout(() => {
        navigate("/api/dashboard/course-table");
        window.location.reload();
      }, 2000);
      // Reset form or redirect as needed
      setFormData({
        title: "",
        instructor: "",
        description: "",
        credit: 0,
        price: 0,
        category: "",
        numberOfCourses: 1,
        courseRating: 0,
        hasPaid: false,
        //  user_id: "",
        lessons: [{ lessonTitle: "", topics: [{ title: "", content: "" }] }],
      });
    } catch (error) {
      console.error("There was an error creating the course:", error);
      throw Error(error || "There was an error creating the course!");
    }
  };

  return (
    <section className="container bg-white text-dark p-4 add-course">
      <div className="row">
        <div className="col-6 mx-auto">
          {loading && (
            <div>
              <Loading />
            </div>
          )}
          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
      </div>
      <div className="row">
        <div className="col">
          <h1>Add New Course</h1>
          <hr />
          <form onSubmit={handleSubmit} className="container mt-5">
            <div className="mb-3">
              <label className="form-label">Course Title:</label>
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Enter course title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />

              {error && formData.title.length <= 0 ? (
                <span className="text-danger">Title is required!</span>
              ) : (
                ""
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Instructor:</label>
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Enter instructor title"
                name="instructor"
                value={formData.instructor}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description:</label>
              <textarea
                className="form-control form-control-lg"
                placeholder="Enter course description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Credit:</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="Enter course credit"
                name="credit"
                value={formData.credit}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Price:</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="$"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="category">
                Course Category:
              </label>
              <select
                className="form-select form-select-lg text-muted"
                name="category"
                id="category"
                value={formData.category}
                onChange={handleChange}
                style={{ fontSize: "14px" }}
              >
                <option disabled>-- Please select one --</option>
                <option defaultValue="IT & Software">IT & Software</option>
                <option defaultValue="Web Development">Web Development</option>
                <option defaultValue="Software Testing">
                  Software Testing
                </option>
                <option defaultValue="Software Engineering">
                  Software Engineering
                </option>
                <option defaultValue="Backend Development">
                  Backend Development
                </option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Number of Courses:</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="Enter number of courses"
                name="numberOfCourses"
                value={formData.numberOfCourses}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Course Rating:</label>
              <input
                type="number"
                step="0.1"
                className="form-control"
                name="courseRating"
                value={formData.courseRating}
                onChange={handleChange}
              />
            </div>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                name="hasPaid"
                checked={formData.hasPaid}
                onChange={handleChange}
              />
              <label className="form-check-label">Has Paid</label>
            </div>

            {formData.lessons.map((lesson, lessonIndex) => (
              <div key={lessonIndex} className="border rounded p-3 mb-3">
                <h4>Lesson {lessonIndex + 1}</h4>
                <div className="mb-3">
                  <label className="form-label">Lesson Title:</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Enter lesson title"
                    name="lessonTitle"
                    value={lesson.lessonTitle}
                    onChange={(e) => handleLessonChange(lessonIndex, e)}
                    required
                  />
                </div>
                {lesson.topics.map((topic, topicIndex) => (
                  <div key={topicIndex} className="mb-3">
                    <label className="form-label">Topic Title:</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Enter topic title"
                      name="title"
                      value={topic.title}
                      onChange={(e) =>
                        handleTopicChange(lessonIndex, topicIndex, e)
                      }
                      required
                    />
                    <label className="form-label">Topic Content:</label>
                    <textarea
                      className="form-control form-control-lg"
                      placeholder="Enter topic content"
                      name="content"
                      value={topic.content}
                      onChange={(e) =>
                        handleTopicChange(lessonIndex, topicIndex, e)
                      }
                      required
                    />
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-lg mb-3"
                  onClick={() => addTopic(lessonIndex)}
                >
                  ADD TOPIC
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-secondary btn-lg mb-3"
              onClick={addLesson}
            >
              ADD LESSON
            </button>
            <div className="mt-4 float-end">
              <button type="submit" className="btn btn-outline-success btn-lg">
                ADD COURSE
              </button>
            </div>
          </form>{" "}
        </div>
      </div>
    </section>
  );
};

export default AddCourse;
